import dayjs from "dayjs";

import {
  DATE_DISPLAY_TYPE,
  formatDateText,
} from "../../../../components/date-display/DateDisplay";

import type { Shot, UpdateShotParams } from "../../../../app/services/shots";
import {
  getShotTableStatusKey,
  isDeliveryDateEditable,
  SHOT_TABLE_STATUS_LABELS,
  SHOT_TABLE_STATUS_ORDER,
  ShotTableStatusKey,
} from "../../../../constant/shotTableStatus";

/** セル編集で PATCH する差分（project_id / shot_id を除いた部分） */
export type ShotPatch = Omit<UpdateShotParams, "project_id" | "shot_id">;

// ── ソート ──────────────────────────────────────────────

export type ShotTableSortField =
  | "shot_name"
  | "status"
  | "next_delivery_date"
  | "updated_at"
  | "media_item_count"
  | "max_take_count"
  | "rating";

export type ShotTableSortOrder = "asc" | "desc";

export interface ShotTableSortState {
  field: ShotTableSortField;
  order: ShotTableSortOrder;
}

/** null を末尾に固定しつつ比較する（asc/desc にかかわらず null は最後） */
const compareNullable = <T>(
  a: T | null,
  b: T | null,
  compare: (x: T, y: T) => number,
  dir: number,
): number => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return compare(a, b) * dir;
};

/**
 * 初期表示のステータス優先順（ポジション別）。
 * - クライアント側: 自分の確認待ち（チェックバック）を先頭に
 * - プロデューサー側: 自分の作業中（制作中）を先頭に
 */
const CLIENT_INITIAL_STATUS_ORDER: ShotTableStatusKey[] = [
  "checkback",
  "in_production",
  "preparing",
  "on_hold",
  "completed",
];
const PRODUCER_INITIAL_STATUS_ORDER: ShotTableStatusKey[] = [
  "in_production",
  "preparing",
  "checkback",
  "on_hold",
  "completed",
];

/** 初期表示用ソート: ポジション別のステータス優先順 → 同ステータス内は最終更新日の新しい順 */
export const sortShotsByInitialOrder = (
  rows: Shot[],
  isClientGroup: boolean,
): Shot[] => {
  const priority = isClientGroup
    ? CLIENT_INITIAL_STATUS_ORDER
    : PRODUCER_INITIAL_STATUS_ORDER;
  return [...rows].sort((a, b) => {
    const ai = priority.indexOf(getShotTableStatusKey(a));
    const bi = priority.indexOf(getShotTableStatusKey(b));
    if (ai !== bi) return ai - bi;
    return compareNullable(
      a.updated_at,
      b.updated_at,
      (x, y) => dayjs(x).diff(dayjs(y)),
      -1,
    );
  });
};

export const sortShotRows = (
  rows: Shot[],
  field: ShotTableSortField,
  order: ShotTableSortOrder,
): Shot[] => {
  const dir = order === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    switch (field) {
      case "shot_name":
        return a.shot_name.localeCompare(b.shot_name, "ja") * dir;
      case "status": {
        const ai = SHOT_TABLE_STATUS_ORDER.indexOf(getShotTableStatusKey(a));
        const bi = SHOT_TABLE_STATUS_ORDER.indexOf(getShotTableStatusKey(b));
        return (ai - bi) * dir;
      }
      case "next_delivery_date":
        return compareNullable(
          a.next_delivery_date,
          b.next_delivery_date,
          (x, y) => dayjs(x).diff(dayjs(y)),
          dir,
        );
      case "updated_at":
        return compareNullable(
          a.updated_at,
          b.updated_at,
          (x, y) => dayjs(x).diff(dayjs(y)),
          dir,
        );
      case "media_item_count":
        return compareNullable(
          a.media_item_count || null,
          b.media_item_count || null,
          (x, y) => x - y,
          dir,
        );
      case "max_take_count":
        return compareNullable(
          a.max_take_count || null,
          b.max_take_count || null,
          (x, y) => x - y,
          dir,
        );
      case "rating":
        return compareNullable(
          a.average_rating || null,
          b.average_rating || null,
          (x, y) => x - y,
          dir,
        );
      default:
        return 0;
    }
  });
};

// ── 日付 ──────────────────────────────────────────────

/**
 * ローカル日時を API 送信用の "YYYY-MM-DDTHH:mm:00Z" に変換する。
 * BE 側は tzinfo を破棄して naive datetime として保存するため、
 * 既存実装（TodoShotCard 等）と同じ「ローカル日時 + Z サフィックス」形式に合わせる。
 */
export const toDeliveryDateParam = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const h = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${d}T${h}:${min}:00Z`;
};

/**
 * テーブル・TSV 表示用の日付フォーマット。soremo 共通の日付表記
 * （`M/D`、他年は `YYYY/M/D`）に揃える。
 */
export const formatTableDate = (iso: string): string =>
  formatDateText(iso, DATE_DISPLAY_TYPE.DATE_ONLY);

export interface ParsedPastedDate {
  ok: boolean;
  /** undefined = セルが空（変更なし） / null は使用しない / string = 設定する値 */
  iso?: string;
}

/**
 * 貼り付けセルの日付文字列をパースする。
 * 対応形式: "2026/6/15" "2026-06-15" "2026年6月15日" "6/15"（年省略は今年）
 * 時刻付き（"2026/6/15 14:00" 等）も受け付ける。時刻省略時は 10:00 を補完
 * （既存の提出予定日モーダルのデフォルト時刻と同じ）。
 */
export const parsePastedDate = (text: string): ParsedPastedDate => {
  const t = text.trim();
  if (!t) return { ok: true, iso: undefined };

  const timeMatch = t.match(/(\d{1,2}):(\d{2})\s*$/);
  const datePart = timeMatch ? t.slice(0, timeMatch.index).trim() : t;

  let year: number;
  let month: number;
  let day: number;

  const full = datePart.match(/^(\d{4})[/\-年.](\d{1,2})[/\-月.](\d{1,2})日?$/);
  const short = datePart.match(/^(\d{1,2})[/\-月.](\d{1,2})日?$/);

  if (full) {
    year = Number(full[1]);
    month = Number(full[2]);
    day = Number(full[3]);
  } else if (short) {
    year = new Date().getFullYear();
    month = Number(short[1]);
    day = Number(short[2]);
  } else {
    return { ok: false };
  }

  const hour = timeMatch ? Number(timeMatch[1]) : 10;
  const minute = timeMatch ? Number(timeMatch[2]) : 0;

  const date = new Date(year, month - 1, day, hour, minute, 0, 0);
  const isRealDate =
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day &&
    hour >= 0 &&
    hour <= 23 &&
    minute >= 0 &&
    minute <= 59;
  if (!isRealDate) return { ok: false };

  return { ok: true, iso: toDeliveryDateParam(date) };
};

// ── TSV / CSV ──────────────────────────────────────────

const SHOT_TABLE_TSV_HEADER = [
  "ショット名",
  "ステータス",
  "次の納品日",
  "最終更新日",
  "メディア数",
  "最大テイク",
  "評価",
];

const sanitizeCell = (value: string): string =>
  value.replace(/[\t\n\r]+/g, " ").trim();

/** テーブル全体を Excel / スプレッドシート互換の TSV に変換する */
export const buildShotsTsv = (rows: Shot[]): string => {
  const lines = rows.map((shot) => {
    const statusKey = getShotTableStatusKey(shot);
    return [
      sanitizeCell(shot.shot_name),
      SHOT_TABLE_STATUS_LABELS[statusKey],
      // 画面表示と同様、納品日は準備中・制作中のみ出力する
      isDeliveryDateEditable(statusKey) && shot.next_delivery_date
        ? formatTableDate(shot.next_delivery_date)
        : "",
      shot.updated_at ? dayjs(shot.updated_at).format("YYYY/M/D H:mm") : "",
      shot.media_item_count ? String(shot.media_item_count) : "",
      shot.max_take_count ? String(shot.max_take_count) : "",
      shot.average_rating ? String(shot.average_rating) : "",
    ].join("\t");
  });
  return [SHOT_TABLE_TSV_HEADER.join("\t"), ...lines].join("\n");
};

/** 1 行を区切り文字で分割する（ダブルクォート対応の簡易パーサ） */
const splitLine = (line: string, delimiter: string): string[] => {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"' && current === "") {
      inQuotes = true;
    } else if (ch === delimiter) {
      cells.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  cells.push(current);
  return cells.map((c) => c.trim());
};

/**
 * クリップボードのテキストを 2 次元配列にパースする。
 * タブを含む場合は TSV（Excel / スプレッドシートのコピー形式）、
 * 含まない場合は CSV として扱う。
 */
export const parseClipboardTable = (text: string): string[][] => {
  const delimiter = text.includes("\t") ? "\t" : ",";
  return text
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .filter((line) => line.trim() !== "")
    .map((line) => splitLine(line, delimiter));
};

/** ヘッダー行（コピーした表の 1 行目など）かどうか */
export const isHeaderRow = (cells: string[]): boolean =>
  (cells[0] ?? "").includes("ショット名");

/** 複数セル（表形式）の貼り付けかどうか */
export const isTabularClipboardText = (text: string): boolean => {
  if (text.includes("\t")) return true;
  const lines = text.replace(/\r\n?/g, "\n").split("\n").filter((l) => l.trim() !== "");
  return lines.length > 1;
};
